/**
 * IssueStripeLinkButton — Issues a Stripe Checkout Session link for a DRAFT order.
 *
 * @see AC-B1 in docs/specifications/2026-05-20-unified-payment-ledger/spec.md
 *
 * UX spec:
 *  - Visible only when order.status === 'DRAFT' AND proforma exists.
 *  - When hasProforma=false: button disabled + aria-describedby tooltip (M-UX3).
 *  - Spinner during loading.
 *  - On success: inline read-only input with URL (monospace) + "Kopiuj link" button.
 *  - "Skopiowano" feedback in aria-live="polite" region (C-9).
 *  - Shows expiresAt as "Link wygaśnie YYYY-MM-DD HH:mm" (M-UX1).
 *  - Focus return on modal close via triggerRef pattern from Phase A.
 *  - WCAG 2.4.3: aria-labelledby pointing to visible h3.
 *  - NIE logujemy URL — tylko sessionId + orderId (M-S6).
 */
import React, { useState, useRef, useEffect, useId } from "react";
import { useIssueStripePaymentLinkMutation } from "../../../../hooks/useAdminProductCatalog";
import type { StripeLinkResponse } from "../../../../types/adminBilling";

// =============================================================================
// CONSTANTS
// =============================================================================

/** How long the "Skopiowano" aria-live feedback remains visible before reset
 *  (MEDIUM Code-Quality — extracted from inline magic number). */
const COPY_FEEDBACK_RESET_MS = 3000;

// =============================================================================
// PROPS
// =============================================================================

export interface IssueStripeLinkButtonProps {
  orderId: string;
  brandId: string;
  /** Only render when DRAFT (spec AC-B1). */
  orderStatus: string;
  /** Whether the order has an issued PROFORMA document (M-UX3). */
  hasProforma: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatExpiry(isoDate: string): string {
  const d = new Date(isoDate);
  const date = d.toLocaleDateString("pl-PL");
  const time = d.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const IssueStripeLinkButton: React.FC<IssueStripeLinkButtonProps> = ({
  orderId,
  brandId,
  orderStatus,
  hasProforma,
}) => {
  const uid = useId();
  const tooltipId = `stripe-link-tooltip-${uid}`;
  const copyFeedbackId = `stripe-link-copy-feedback-${uid}`;

  const [linkData, setLinkData] = useState<StripeLinkResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useIssueStripePaymentLinkMutation(orderId, brandId);

  // Reset state when orderId changes (modal re-opened with different order)
  useEffect(() => {
    setLinkData(null);
    setCopied(false);
  }, [orderId]);

  // Only show for DRAFT orders (spec: button exists alongside "Zaksięguj płatność przelewem")
  if (orderStatus !== "DRAFT") {
    return null;
  }

  const handleIssue = async () => {
    try {
      const result = await mutation.mutateAsync();
      setLinkData(result);
      setCopied(false);
    } catch {
      // Error already surfaced via mutation onError toast (mapped to user-friendly
      // messages for 409 PROFORMA_REQUIRED / 409 ORDER_NOT_DRAFT / 422 timeout).
      // Swallow here to prevent unhandled rejection — button returns to idle.
    }
  };

  const handleCopy = async () => {
    if (!linkData) return;
    try {
      await navigator.clipboard.writeText(linkData.url);
      setCopied(true);
      // Auto-reset after COPY_FEEDBACK_RESET_MS
      setTimeout(() => setCopied(false), COPY_FEEDBACK_RESET_MS);
    } catch {
      // Fallback: select input text
      inputRef.current?.select();
    }
  };

  // ---- Post-success view ----
  if (linkData) {
    return (
      <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-md space-y-2">
        <p className="text-xs font-medium text-primary-800">
          Link Stripe gotowy
        </p>
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            readOnly
            value={linkData.url}
            aria-label="Link do płatności Stripe"
            className="flex-1 font-mono text-xs border border-primary-300 bg-white rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-600 truncate"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="px-3 py-1 text-xs bg-primary-700 text-white rounded-md hover:bg-primary-800 whitespace-nowrap"
          >
            Kopiuj link
          </button>
        </div>
        {/* aria-live="polite" copy feedback (C-9) */}
        <p
          id={copyFeedbackId}
          aria-live="polite"
          className={`text-xs ${copied ? "text-green-700 font-medium" : "text-transparent"}`}
        >
          {copied ? "Skopiowano" : " "}
        </p>
        {/* expiresAt countdown (M-UX1) */}
        <p className="text-xs text-primary-700">
          Link wygaśnie {formatExpiry(linkData.expiresAt)}
        </p>
      </div>
    );
  }

  // ---- Issue button ----
  const tooltipText = !hasProforma
    ? "Wygeneruj fakturę proforma przed wystawieniem linku Stripe"
    : "";

  return (
    <div className="relative inline-block group">
      <button
        type="button"
        onClick={handleIssue}
        disabled={mutation.isPending || !hasProforma}
        aria-describedby={!hasProforma ? tooltipId : undefined}
        className={`px-4 py-2 text-sm border border-primary-300 text-primary-800 rounded-md hover:bg-primary-50 flex items-center gap-2 ${
          mutation.isPending || !hasProforma ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {mutation.isPending && (
          <span
            className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"
            aria-hidden="true"
          />
        )}
        Wystaw Stripe link
      </button>

      {/* Tooltip for disabled state (M-UX3 + UX HIGH fix: visible on hover/focus).
          Combines visible visual tooltip with aria-describedby for screen readers. */}
      {!hasProforma && (
        <span
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-10 whitespace-nowrap rounded-md bg-neutral-900 px-3 py-1.5 text-xs text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
        >
          {tooltipText}
        </span>
      )}
    </div>
  );
};
